import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db6bof4be24agvuu3ht0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Read honeypot before any async work.
    const honeypot = String(data.get("website_alt") ?? "").trim();

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    if (honeypot) {
      // Silently treat as success so bots get no signal.
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const okCode = parsed?.code === "OK";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || !okCode || isSpam) {
        setStatus("error");
        setFormError(
          !isSpam && serverMsg ? String(serverMsg) : "提交失败，请检查内容后重试"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-chat-3-line text-primary-500 leading-none"></i>
        意见反馈
      </h3>
      <p className="mt-1.5 text-sm text-foreground-600">
        对网站有建议、内容纠错或合作意向，欢迎留言告诉我们。
      </p>

      <form
        id="about-feedback-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-5 space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
              称呼
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              required
              placeholder="请输入您的称呼"
              className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
              邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              placeholder="用于接收回复"
              className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div>
          <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
            反馈类型
          </label>
          <select
            id="fb-topic"
            name="topic"
            defaultValue="内容建议"
            className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="内容建议">内容建议</option>
            <option value="内容纠错">内容纠错</option>
            <option value="功能反馈">功能反馈</option>
            <option value="商务合作">商务合作</option>
          </select>
        </div>

        <div>
          <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
            留言内容
          </label>
          <textarea
            id="fb-message"
            name="message"
            rows={4}
            maxLength={500}
            required
            placeholder="请输入您的建议或问题（不超过 500 字）"
            className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-guard-message"
        />

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} leading-none`}></i>
            {status === "submitting" ? "提交中…" : "提交反馈"}
          </button>

          {status === "success" && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
              <i className="ri-checkbox-circle-line leading-none"></i>
              提交成功，感谢你的反馈！
            </span>
          )}
          {status === "error" && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700">
              <i className="ri-error-warning-line leading-none"></i>
              {formError}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}